
import { useState, useEffect, useCallback } from 'react';

export interface Task {
  id: string;
  text: string;
  completed: boolean;
  pomodorosCompleted: number;
}

export type TimerMode = 'focus' | 'break';

// Pre-populated tasks for working mothers
const defaultWorkingMotherTasks: Omit<Task, 'id'>[] = [
  { text: "Prepare quarterly budget presentation for board meeting", completed: false, pomodorosCompleted: 0 },
  { text: "Review and respond to 15+ client emails from overnight", completed: false, pomodorosCompleted: 0 },
  { text: "Schedule pediatrician appointments for both kids' annual checkups", completed: false, pomodorosCompleted: 0 },
  { text: "Plan and prep healthy meals for the entire week", completed: false, pomodorosCompleted: 0 },
  { text: "Research and compare daycare options for youngest child", completed: false, pomodorosCompleted: 0 },
  { text: "Complete performance reviews for 3 team members", completed: false, pomodorosCompleted: 0 },
  { text: "Organize kids' school supplies and backpacks for new semester", completed: false, pomodorosCompleted: 0 },
  { text: "Update family budget and review monthly expenses", completed: false, pomodorosCompleted: 0 },
  { text: "Coordinate carpools with other parents for soccer practice", completed: false, pomodorosCompleted: 0 },
  { text: "Prepare for parent-teacher conferences next week", completed: false, pomodorosCompleted: 0 },
  { text: "Research summer camp options and submit applications", completed: false, pomodorosCompleted: 0 },
  { text: "Schedule family dentist appointments for everyone", completed: false, pomodorosCompleted: 0 },
  { text: "Plan birthday party for 8-year-old (venue, cake, invitations)", completed: false, pomodorosCompleted: 0 },
  { text: "Update emergency contact lists for school and daycare", completed: false, pomodorosCompleted: 0 },
  { text: "Organize and declutter kids' closets for season change", completed: false, pomodorosCompleted: 0 },
  { text: "Research and book family vacation for spring break", completed: false, pomodorosCompleted: 0 },
  { text: "Complete online training modules for work certification", completed: false, pomodorosCompleted: 0 },
  { text: "Schedule 30-minute self-care time (bath, reading, meditation)", completed: false, pomodorosCompleted: 0 },
  { text: "Coordinate babysitter for date night this weekend", completed: false, pomodorosCompleted: 0 },
  { text: "Update family calendar with all activities and appointments", completed: false, pomodorosCompleted: 0 }
];

export function usePomodoro() {
  const [tasks, setTasks] = useState<Task[]>([]);
  const [activeTaskId, setActiveTaskId] = useState<string | null>(null);
  const [timerMode, setTimerMode] = useState<TimerMode>('focus');
  const [timeLeft, setTimeLeft] = useState(25 * 60); // 25 minutes in seconds
  const [isRunning, setIsRunning] = useState(false);
  const [filter, setFilter] = useState<'all' | 'active' | 'completed'>('all');

  // Load tasks from localStorage on mount
  useEffect(() => {
    const savedTasks = localStorage.getItem('pomodoro-tasks');
    const hasLoadedBefore = localStorage.getItem('pomodoro-initialized');
    
    if (savedTasks && hasLoadedBefore) {
      try {
        const parsedTasks = JSON.parse(savedTasks);
        setTasks(parsedTasks);
      } catch (error) {
        console.error('Error loading tasks:', error);
      }
    } else {
      // First time loading - add default working mother tasks
      const initialTasks: Task[] = defaultWorkingMotherTasks.map((task, index) => ({
        ...task,
        id: (Date.now() + index).toString(),
      }));
      
      setTasks(initialTasks);
      localStorage.setItem('pomodoro-tasks', JSON.stringify(initialTasks));
      localStorage.setItem('pomodoro-initialized', 'true');
    }
  }, []);

  // Save tasks to localStorage whenever tasks change
  const saveTasks = useCallback((newTasks: Task[]) => {
    localStorage.setItem('pomodoro-tasks', JSON.stringify(newTasks));
  }, []);

  // Timer logic
  useEffect(() => {
    let interval: NodeJS.Timeout;

    if (isRunning && timeLeft > 0) {
      interval = setInterval(() => {
        setTimeLeft((prev) => prev - 1);
      }, 1000);
    } else if (timeLeft === 0) {
      // Timer completed
      playAudioCue();
      
      if (timerMode === 'focus' && activeTaskId) {
        // Complete the active task and increment pomodoros
        setTasks((prevTasks) => {
          const updatedTasks = prevTasks.map((task) =>
            task.id === activeTaskId
              ? { ...task, completed: true, pomodorosCompleted: task.pomodorosCompleted + 1 }
              : task
          );
          saveTasks(updatedTasks);
          return updatedTasks;
        });
        
        // Switch to break mode
        setTimerMode('break');
        setTimeLeft(5 * 60); // 5 minutes break
        setIsRunning(true); // Auto-start break
      } else if (timerMode === 'break') {
        // Break completed, switch back to focus
        setTimerMode('focus');
        setTimeLeft(25 * 60); // 25 minutes focus
        setIsRunning(false);
      }
    }

    return () => clearInterval(interval);
  }, [isRunning, timeLeft, timerMode, activeTaskId, saveTasks]);

  const playAudioCue = () => {
    // Create a simple beep sound using Web Audio API
    try {
      const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);
      
      oscillator.frequency.value = 800;
      oscillator.type = 'sine';
      
      gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.5);
      
      oscillator.start(audioContext.currentTime);
      oscillator.stop(audioContext.currentTime + 0.5);
    } catch (error) {
      console.log('Audio notification not available');
    }
  };

  const addTask = (text: string) => {
    const newTask: Task = {
      id: Date.now().toString(),
      text: text.trim(),
      completed: false,
      pomodorosCompleted: 0,
    };
    
    const updatedTasks = [...tasks, newTask];
    setTasks(updatedTasks);
    saveTasks(updatedTasks);
  };

  const deleteTask = (id: string) => {
    const updatedTasks = tasks.filter((task) => task.id !== id);
    setTasks(updatedTasks);
    saveTasks(updatedTasks);
    
    if (activeTaskId === id) {
      setActiveTaskId(null);
    }
  };

  const toggleTaskComplete = (id: string) => {
    const updatedTasks = tasks.map((task) =>
      task.id === id ? { ...task, completed: !task.completed } : task
    );
    setTasks(updatedTasks);
    saveTasks(updatedTasks);
  };

  const setActiveTask = (id: string) => {
    setActiveTaskId(id);
  };

  const startTimer = () => {
    setIsRunning(true);
  };

  const pauseTimer = () => {
    setIsRunning(false);
  };

  const resetTimer = () => {
    setIsRunning(false);
    setTimeLeft(timerMode === 'focus' ? 25 * 60 : 5 * 60);
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const filteredTasks = tasks.filter((task) => {
    if (filter === 'active') return !task.completed;
    if (filter === 'completed') return task.completed;
    return true;
  });

  const activeTask = tasks.find((task) => task.id === activeTaskId);
  const totalPomodoros = tasks.reduce((sum, task) => sum + task.pomodorosCompleted, 0);

  return {
    tasks: filteredTasks,
    activeTask,
    activeTaskId,
    timerMode,
    timeLeft,
    isRunning,
    filter,
    totalPomodoros,
    addTask,
    deleteTask,
    toggleTaskComplete,
    setActiveTask,
    startTimer,
    pauseTimer,
    resetTimer,
    formatTime,
    setFilter,
  };
}
